import { z } from "zod";
import type { TransformDefinition } from "./transforms";

/**
 * A plain object, or absent. Arrays are deliberately EXCLUDED, for the same
 * reason `fallback` excludes them: `evaluateTransform` picks whole vs per-item
 * evaluation by fit, so an object schema that accepted arrays would read a
 * field off the batch itself (`undefined`) instead of off each row. Rejecting
 * arrays is what makes `(get rows "email")` map over the rows.
 */
const OBJECT_OR_ABSENT = z.union([
  z.record(z.string(), z.unknown()),
  z.null(),
  z.undefined(),
]);

/**
 * get — `(get value "field")`. Yields one field of an object, or `undefined`
 * when the object is absent or has no such field of its own. A single field
 * name, not a path: nest calls to reach deeper, `(get (get v "a") "b")`.
 *
 * Own properties only, so `"__proto__"` / `"constructor"` read as missing
 * rather than reaching the prototype. Pure and total — a missing field is an
 * absent value for `fallback` to fill, not an error.
 */
export const getFieldTransform = {
  name: "get",
  description:
    "Yields one field of an object — per row when given a list of rows.",
  args: z.tuple([OBJECT_OR_ABSENT, z.string().min(1)]),
  output: z.unknown(),
  evaluate: ([value, field]) => {
    // Args arrive already validated against the tuple above; the registry's
    // erased typing just cannot say so.
    if (typeof value !== "object" || value === null) return undefined;
    if (typeof field !== "string") return undefined;
    // An own data property only — never the prototype chain.
    return Object.getOwnPropertyDescriptor(value, field)?.value;
  },
} satisfies TransformDefinition;
